// notificationsDropdown.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInView } from 'react-intersection-observer';

import { ArrowLeft, Check, Settings } from 'lucide-react';

import {
  Box,
  Button,
  CircularProgress,
  debounce,
  Divider,
  IconButton,
  List,
  ListItem,
  Switch,
} from '@mui/material';

import { queryNotifications } from '@itp/prisma-talent/gql';
import { usePrivateApollo, useSocket, useUser } from '@itp/talent';
import { SHADCN_COLORS, theme } from '@itp/talent/theme';

import type { NotificationGroup, NotificationType } from './types';

import { BodyText, HeadingMedium } from '../../core/typography';
import GeneralNotification from './generalNotification';
import NotificationsGroup from './notificationsGroup';
import {
  getNotificationVisualKey,
  groupNotifications,
  paginationUpdater,
  readUpdater,
} from './utils';

import './styles.css';

const PER_PAGE = 30;
const DEFAULT_SOCKET_DEBOUNCE = 300;

interface NotificationState {
  notifications: NotificationGroup[];
  totalLoaded: number;
}

const NotificationItem: React.FC<{
  notification: NotificationType;
  onViewEnter: (id: string, read: boolean) => void;
}> = ({ notification, onViewEnter }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        onViewEnter(notification.id, Boolean(notification.readAt));
      }
    },
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.5,
    triggerOnce: true,
  });
  const read = Boolean(notification.readAt);
  return (
    <ListItem
      sx={{
        backgroundColor: read ? 'transparent' : SHADCN_COLORS.muted,
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: read ? '4px solid transparent' : '4px solid #bdbdbd',
        py: 2,
      }}
      data-notification-id={notification.id}
      data-read={read}
      key={notification.id}
      ref={ref}
    >
      <GeneralNotification notification={notification} />
    </ListItem>
  );
};

const SettingsView: React.FC<{
  filters: Set<string>;
  groupingEnabled: boolean;
  onClose: () => void;
  onMarkAllRead: () => void;
  setFilters: (value: Set<string>) => void;
  setGroupingEnabled: (value: boolean) => void;
}> = ({ filters, groupingEnabled, onClose, onMarkAllRead, setFilters, setGroupingEnabled }) => {
  const filterTypes = [
    { label: 'Applications', value: 'talentJobApplication' },
    { label: 'Invitations', value: 'talentInvitation' },
    { label: 'Job Ads', value: 'jobAd' },
    { label: 'Team changes', value: 'companyOnUser' },
    { label: 'Assignments', value: 'jobAssignment' },
    { label: 'New users', value: 'user' },
  ];

  const handleFilterToggle = (value: string) => {
    const newFilters = new Set(filters);
    if (newFilters.has(value)) {
      newFilters.delete(value);
    } else {
      newFilters.add(value);
    }
    setFilters(newFilters);
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} p={2}>
      <Button
        onClick={() => {
          onMarkAllRead();
          onClose();
        }}
        fullWidth
        startIcon={<Check size={16} />}
        variant="contained"
      >
        Mark all as read
      </Button>

      <Divider />

      <Box>
        <BodyText mb={1.5}>Filter by type</BodyText>
        <Box display="flex" flexDirection="column">
          {filterTypes.map((type) => (
            <Box
              alignItems="center"
              display="flex"
              justifyContent="space-between"
              key={type.value}
              py={0.5}
            >
              <BodyText>{type.label}</BodyText>
              <Switch
                checked={filters.has(type.value)}
                onChange={() => handleFilterToggle(type.value)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Divider />

      <Box alignItems="center" display="flex" justifyContent="space-between">
        <BodyText>Group notifications</BodyText>
        <Switch checked={groupingEnabled} onChange={(e) => setGroupingEnabled(e.target.checked)} />
      </Box>
    </Box>
  );
};

export const NotificationsDropdown: React.FC<{ show: boolean }> = ({ show }) => {
  const { user } = useUser();
  const socket = useSocket();
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState<Set<string>>(new Set());
  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const [notificationState, setNotificationState] = useState<NotificationState>({
    notifications: [],
    totalLoaded: 0,
  });
  const [hasMore, setHasMore] = useState(true);
  const [isMoreLoading, setIsMoreLoading] = useState(true);
  const notificationIdsToRead = useRef<string[]>([]);
  const notificationStateRef = useRef(notificationState);
  notificationStateRef.current = notificationState;

  const readDebounce = useRef(
    debounce(() => {
      if (socket && notificationIdsToRead.current.length > 0) {
        socket.emit('notification-update', {
          data: { readAt: new Date() },
          where: { id: { in: notificationIdsToRead.current } },
        });
        setNotificationState((prev) => ({
          ...prev,
          notifications:
            readUpdater({
              notificationIds: notificationIdsToRead.current,
              state: prev.notifications,
            }) || [],
        }));
        notificationIdsToRead.current = [];
      }
    }, 100),
  ).current;

  const onViewEnter = useCallback(
    (id: string, read: boolean) => {
      if (!read && !notificationIdsToRead.current.includes(id)) {
        notificationIdsToRead.current.push(id);
        readDebounce();
      }
    },
    [readDebounce],
  );

  const { executeQuery, loading } = usePrivateApollo<NotificationType[]>({
    query: queryNotifications,
    skip: !show || !user?.id,
    variables: {
      orderBy: [{ createdAt: 'desc' }],
      skip: 0,
      take: PER_PAGE,
      where: {
        toId: { equals: user?.id },
      },
    },
  });

  const handleFetchResponse = useCallback(
    (newNotifications: NotificationType[], replace = false) => {
      const groupedNotifications = groupNotifications(newNotifications);
      setNotificationState((prev) => ({
        notifications: paginationUpdater({
          data: groupedNotifications,
          replace,
          state: prev.notifications,
        }),
        totalLoaded: replace ? newNotifications.length : prev.totalLoaded + newNotifications.length,
      }));
      setIsMoreLoading(false);
      setHasMore(newNotifications.length === PER_PAGE);
    },
    [],
  );

  const fetchNotifications = useCallback(
    async (page: number, replace = false) => {
      const skip = page * PER_PAGE;
      const { data: response } = await executeQuery({
        variables: {
          orderBy: [{ createdAt: 'desc' }],
          skip,
          take: PER_PAGE,
          where: {
            toId: { equals: user?.id },
          },
        },
      });
      const notifications = response?.data || [];
      handleFetchResponse(notifications, replace);
      return notifications;
    },
    [executeQuery, user?.id, handleFetchResponse],
  );

  const fetchNotificationsRef = useRef(fetchNotifications);
  fetchNotificationsRef.current = fetchNotifications;

  const loadMore = useCallback(() => {
    const nextPage = Math.floor(notificationState.totalLoaded / PER_PAGE);
    setIsMoreLoading(true);
    fetchNotifications(nextPage);
  }, [fetchNotifications, notificationState.totalLoaded]);

  const refreshNotifications = useCallback(() => {
    setNotificationState({ notifications: [], totalLoaded: 0 });
    setHasMore(true);
    fetchNotifications(0, true);
  }, [fetchNotifications]);

  const handleMarkAllRead = useCallback(() => {
    const allUnreadIds = notificationState.notifications
      .flatMap((g) => g.items)
      .filter((n) => !n.readAt)
      .map((n) => n.id);

    if (socket && allUnreadIds.length > 0) {
      socket.emit('notification-update', {
        data: { readAt: new Date() },
        where: { id: { in: allUnreadIds } },
      });

      setNotificationState((prev) => ({
        ...prev,
        notifications:
          readUpdater({
            notificationIds: allUnreadIds,
            state: prev.notifications,
          }) || [],
      }));
    }
  }, [socket, notificationState.notifications]);

  const filteredNotifications = useMemo(() => {
    if (filters.size === 0) return notificationState.notifications;

    return notificationState.notifications
      .map((group) => ({
        ...group,
        items: group.items.filter((n) => {
          const eventModel = n.event?.eventData?.model;
          return filters.has(eventModel);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [notificationState.notifications, filters]);

  const displayNotifications = useMemo(() => {
    if (groupingEnabled) return filteredNotifications;

    const seen = new Set<string>();
    return filteredNotifications.flatMap((group) =>
      group.items
        .filter((item) => {
          const key = getNotificationVisualKey(item);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((item) => ({
          id: item.id,
          items: [item],
          type: group.type,
        })),
    );
  }, [filteredNotifications, groupingEnabled]);

  useEffect(() => {
    if (!socket) return;

    let notificationCreatedTimer: null | ReturnType<typeof setTimeout> = null;
    let notificationUpdatedTimer: null | ReturnType<typeof setTimeout> = null;

    const handleNotificationCreated = () => {
      if (notificationCreatedTimer) clearTimeout(notificationCreatedTimer);
      notificationCreatedTimer = setTimeout(async () => {
        const newData = await fetchNotificationsRef.current(0);
        const existingIds = new Set(
          notificationStateRef.current.notifications.flatMap((g) => g.items).map((n) => n.id),
        );
        const newNotifications = newData.filter((n: NotificationType) => !existingIds.has(n.id));
        if (newNotifications.length) {
          const groupedNew = groupNotifications(newNotifications);
          setNotificationState((prev) => ({
            notifications: paginationUpdater({
              data: groupedNew,
              prepend: true,
              state: prev.notifications,
            }),
            totalLoaded: prev.totalLoaded + newNotifications.length,
          }));
        }
      }, DEFAULT_SOCKET_DEBOUNCE);
    };

    const handleNotificationUpdated = (payload: { where: { id: { in: string[] } } }) => {
      if (notificationUpdatedTimer) clearTimeout(notificationUpdatedTimer);
      notificationUpdatedTimer = setTimeout(() => {
        setNotificationState((prev) => ({
          ...prev,
          notifications:
            readUpdater({ notificationIds: payload.where.id.in, state: prev.notifications }) || [],
        }));
      }, DEFAULT_SOCKET_DEBOUNCE);
    };

    socket.on('notification-created', handleNotificationCreated);
    socket.on('notification-updated', handleNotificationUpdated);

    return () => {
      if (notificationCreatedTimer) clearTimeout(notificationCreatedTimer);
      if (notificationUpdatedTimer) clearTimeout(notificationUpdatedTimer);
      socket.off('notification-created');
      socket.off('notification-updated');
    };
  }, [socket]);

  useEffect(() => {
    if (show && user?.id) {
      const loadInitial = async () => {
        await fetchNotifications(0, true);
      };
      loadInitial();
    } else {
      setNotificationState({ notifications: [], totalLoaded: 0 });
      setHasMore(false);
    }
  }, [show, user?.id, fetchNotifications]);

  if (!show) return null;

  return (
    <Box sx={{ height: '100%', p: 0 }}>
      {!showSettings && notificationState.notifications.length > 0 && (
        <>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            p={theme.spacing(2)}
          >
            <HeadingMedium>Notifications</HeadingMedium>
            <IconButton onClick={() => setShowSettings(true)} size="small">
              <Settings size={18} />
            </IconButton>
          </Box>
          <Divider />
        </>
      )}

      {showSettings && (
        <>
          <Box alignItems="center" display="flex" gap={1} p={theme.spacing(2)}>
            <IconButton onClick={() => setShowSettings(false)} size="small">
              <ArrowLeft size={18} />
            </IconButton>
            <HeadingMedium>Settings</HeadingMedium>
          </Box>
          <Divider />
        </>
      )}

      <List id="notifications-scroll" sx={{ height: '100%' }}>
        {showSettings ? (
          <SettingsView
            filters={filters}
            groupingEnabled={groupingEnabled}
            onClose={() => setShowSettings(false)}
            onMarkAllRead={handleMarkAllRead}
            setFilters={setFilters}
            setGroupingEnabled={setGroupingEnabled}
          />
        ) : (
          <InfiniteScroll
            endMessage={
              <Box alignItems="center" display="flex" justifyContent="center" py={2}>
                <BodyText>No more notifications</BodyText>
              </Box>
            }
            loader={
              isMoreLoading && notificationState.notifications.length ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <></>
              )
            }
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100dvh - 80px)',
            }}
            dataLength={notificationState.totalLoaded}
            hasMore={hasMore}
            next={loadMore}
            pullDownToRefresh
            pullDownToRefreshThreshold={50}
            refreshFunction={refreshNotifications}
            scrollableTarget="notifications-scroll"
          >
            {loading && !notificationState.notifications.length ? (
              <Box alignItems="center" display="flex" height="100%" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : displayNotifications.length ? (
              displayNotifications.map((item) => {
                if (groupingEnabled && item.items.length > 1) {
                  return (
                    <NotificationsGroup group={item} key={item.id} onViewEnter={onViewEnter} />
                  );
                } else {
                  const notification = item.items[0];
                  return (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onViewEnter={onViewEnter}
                    />
                  );
                }
              })
            ) : (
              <></>
            )}
          </InfiniteScroll>
        )}
      </List>
    </Box>
  );
};
